import {
  CompleteIntegrationConnectionInput,
  Graphback,
  IntegrationProvider,
  graphQLErrorCode,
  useAuthentication,
  useCompleteIntegrationConnection,
} from '@gripp/shared-logic';
import { Button, Colors, tokens } from '@gripp/shared-ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { useNavigate } from 'react-router-dom';
import {
  INTEGRATION_CALLBACK_PATH,
  IntegrationCallbackInput,
  takeIntegrationCallbackInput,
} from '../../../shared/logging/integrationCallbackPrivacy';

type CallbackStatus =
  | 'loading'
  | 'success'
  | 'providerError'
  | 'invalidAttempt'
  | 'forbidden'
  | 'failed'
  | 'busy'
  | 'signedOut';

const failureStatus = (error: unknown): CallbackStatus => {
  switch (graphQLErrorCode(error)) {
    case 'FORBIDDEN':
      return 'forbidden';
    case 'INVALID_AUTH_STATE':
    case 'MISSING_AUTHORIZATION_CODE':
    case 'OAUTH_INVALID_GRANT':
      return 'invalidAttempt';
    case 'INTEGRATION_CONNECTION_BUSY':
      return 'busy';
    default:
      return 'failed';
  }
};

export const IntegrationCallback = () => {
  const authentication = useAuthentication();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const input = useRef<IntegrationCallbackInput | null | undefined>(undefined);
  const started = useRef(false);
  const mounted = useRef(false);
  const [payload, setPayload] =
    useState<CompleteIntegrationConnectionInput | null>(null);
  const [status, setStatus] = useState<CallbackStatus>('loading');
  const { mutateAsync: complete, reset } =
    useCompleteIntegrationConnection(payload);

  useEffect(() => {
    mounted.current = true;
    // The reader is destructive. StrictMode repeats effect setup, so the ref
    // must guard consumption; render and state initializers are not safe.
    if (input.current === undefined) {
      input.current = takeIntegrationCallbackInput();
      navigate(INTEGRATION_CALLBACK_PATH, { replace: true, state: null });
      if (input.current?.kind === 'complete') {
        setPayload({
          provider: IntegrationProvider.CaseIh,
          code: input.current.code,
          state: input.current.state,
        });
      } else {
        setStatus(
          input.current?.kind === 'provider-error'
            ? 'providerError'
            : 'invalidAttempt'
        );
      }
    }
    return () => {
      mounted.current = false;
    };
  }, [navigate]);

  useEffect(() => {
    if (!authentication || authentication.isLoading) return;
    if (!authentication.isAuthenticated || !authentication.accessToken) {
      input.current = null;
      setPayload(null);
      setStatus('signedOut');
      return;
    }
    if (!payload || started.current) return;

    // The server resolves and authorizes the ledger's workspace. The active
    // workspace can change during consent; pane/start own the feature gate.
    started.current = true;
    Graphback.initialize(authentication.accessToken);
    const finish = async (): Promise<void> => {
      try {
        await complete();
        if (mounted.current) setStatus('success');
      } catch (error: unknown) {
        if (mounted.current) setStatus(failureStatus(error));
      } finally {
        input.current = null;
        if (mounted.current) {
          setPayload(null);
          reset();
        }
      }
    };
    void finish();
  }, [authentication, payload, complete, reset]);

  const visibleStatus =
    !authentication || authentication.isLoading
      ? 'loading'
      : !authentication.isAuthenticated || !authentication.accessToken
      ? 'signedOut'
      : status;
  const loading = visibleStatus === 'loading';
  const signedOut = visibleStatus === 'signedOut';
  const success = visibleStatus === 'success';

  return (
    <View style={styles.container} testID="integration-callback">
      <View style={styles.content}>
        <Text accessibilityRole="header" style={styles.title}>
          {t(
            `integrationCallback.${
              loading ? 'title' : success ? 'successTitle' : 'failureTitle'
            }`
          )}
        </Text>
        {loading && (
          <ActivityIndicator
            size="large"
            color={Colors.role.brandOnSurface}
            accessibilityLabel={t('integrationCallback.loading')}
          />
        )}
        <Text accessibilityLiveRegion="polite" style={styles.message}>
          {t(`integrationCallback.${visibleStatus}`)}
        </Text>
        {!loading && !success && !signedOut && (
          <Text style={styles.message}>
            {t('integrationCallback.retryHint')}
          </Text>
        )}
        {!loading && (
          <Button
            size="large"
            state="active"
            variant="rectangular"
            style={styles.actionButton}
            testID="integration-callback-continue"
            onPress={() =>
              navigate(signedOut ? '/login' : '/settings', { replace: true })
            }
          >
            {t(`integrationCallback.${signedOut ? 'signIn' : 'settings'}`)}
          </Button>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacing.lg,
    backgroundColor: Colors.role.canvas,
  },
  content: {
    width: 340,
    maxWidth: '100%',
    gap: tokens.spacing.lg,
  },
  title: {
    ...tokens.typography.title.smallEmphasised,
    color: Colors.role.textStrong,
  },
  message: {
    ...tokens.typography.body.medium,
    color: Colors.role.textSecondary,
  },
  actionButton: {
    width: '100%',
    height: 48,
    borderRadius: tokens.borderRadius.sm,
  },
});
